import { atom } from 'jotai';
import { atomWithStorage, useResetAtom } from 'jotai/utils';

import { storage } from '@service-initiation/shared-utils-jotai';

import { DeliveryMethod } from '../types';
import {
  appointmentIntentAtom,
  useResetAppointmentIntent,
} from './appointmentIntentAtom';
import { asapAppointmentAtom } from './asapAppointmentAtom';
import { deliveryMethodAsyncAtom } from './deliveryMethodAsyncAtom';
import { firstAvailableAppointmentAsyncAtom } from './firstAvailableAppointmentAsyncAtom';
import { selectedStoreAsyncAtom } from './selectedStoreAsyncAtom';

export type SelectedAppointment = {
  date?: string;
  endTime?: string;
  intentId?: string;
  isAsap?: boolean;
  startTime?: string;
};

export type AppointmentValue = {
  date: string;
  endTime?: string;
  isAsap?: never;
  startTime?: string;
};

export type IsAsapValue = {
  date?: never;
  endTime?: never;
  isAsap: boolean;
  startTime?: never;
};

type SetterValue = AppointmentValue | IsAsapValue;

function isAppointmentValue(value: SetterValue): value is AppointmentValue {
  return (
    (value as AppointmentValue).date !== undefined ||
    (value as AppointmentValue).startTime !== undefined ||
    (value as AppointmentValue).endTime !== undefined
  );
}

function isIsAsapValue(value: SetterValue): value is IsAsapValue {
  return (value as IsAsapValue).isAsap !== undefined;
}

const selectedAppointmentDateAtomWithStorage = atomWithStorage<
  string | undefined
>('si-tec-car-appointment-date', undefined, storage<string | undefined>(), {
  getOnInit: true,
});

const selectedAppointmentEndTimeAtomWithStorage = atomWithStorage<
  string | undefined
>('si-tec-car-appointment-end-time', undefined, storage<string | undefined>(), {
  getOnInit: true,
});

const selectedAppointmentStartTimeAtomWithStorage = atomWithStorage<
  string | undefined
>(
  'si-tec-car-appointment-start-time',
  undefined,
  storage<string | undefined>(),
  {
    getOnInit: true,
  }
);

const appointmentIsAsapAtomWithStorage = atomWithStorage<boolean | undefined>(
  'si-tec-car-appointment-is-asap',
  undefined,
  storage<boolean | undefined>(),
  {
    getOnInit: true,
  }
);

export const selectedAppointmentAsyncAtom = atom<
  Promise<SelectedAppointment>,
  [SetterValue],
  Promise<void>
>(
  async (get) => {
    const appointmentIsAsap = get(appointmentIsAsapAtomWithStorage);

    if (appointmentIsAsap) {
      const asapAppointment = await get(asapAppointmentAtom);

      return { ...asapAppointment, isAsap: true };
    }

    return {
      date:
        get(selectedAppointmentDateAtomWithStorage) ||
        (await get(firstAvailableAppointmentAsyncAtom))?.date,
      endTime: get(selectedAppointmentEndTimeAtomWithStorage),
      intentId: get(appointmentIntentAtom)?.id,
      isAsap: false,
      startTime: get(selectedAppointmentStartTimeAtomWithStorage),
    };
  },
  async (get, set, value) => {
    const selectedStore = await get(selectedStoreAsyncAtom);
    const deliveryMethod = await get(deliveryMethodAsyncAtom);
    if (isIsAsapValue(value)) {
      set(appointmentIsAsapAtomWithStorage, value.isAsap);
      const asapAppointment = await get(asapAppointmentAtom);
      if (
        deliveryMethod === DeliveryMethod.VisitStore &&
        selectedStore &&
        asapAppointment?.date &&
        asapAppointment.startTime &&
        asapAppointment.endTime
      ) {
        await set(appointmentIntentAtom, {
          date: asapAppointment.date,
          endTime: asapAppointment.endTime,
          startTime: asapAppointment.startTime,
          storeId: selectedStore.id,
          timeZone: selectedStore.time_zone,
        });
      }
    } else if (isAppointmentValue(value)) {
      set(selectedAppointmentDateAtomWithStorage, value.date);
      set(selectedAppointmentStartTimeAtomWithStorage, value.startTime);
      set(selectedAppointmentEndTimeAtomWithStorage, value.endTime);
      set(appointmentIsAsapAtomWithStorage, false);

      if (
        deliveryMethod === DeliveryMethod.VisitStore &&
        value.endTime &&
        value.startTime &&
        value.date &&
        selectedStore
      ) {
        await set(appointmentIntentAtom, {
          date: value.date,
          endTime: value.endTime,
          startTime: value.startTime,
          storeId: selectedStore.id,
          timeZone: selectedStore.time_zone,
        });
      }
    }
  }
);

export const useResetSelectedAppointment = () => {
  const resetIntent = useResetAppointmentIntent();
  const resetIsAsap = useResetAtom(appointmentIsAsapAtomWithStorage);
  const resetDate = useResetAtom(selectedAppointmentDateAtomWithStorage);
  const resetEndTime = useResetAtom(selectedAppointmentEndTimeAtomWithStorage);
  const resetStartTime = useResetAtom(
    selectedAppointmentStartTimeAtomWithStorage
  );

  return () => {
    resetIntent();
    resetIsAsap();
    resetDate();
    resetEndTime();
    resetStartTime();
  };
};
